import React from 'react';
import { FiArrowLeft, FiLock } from 'react-icons/fi';
import { Link } from '@tanstack/react-router';

const logo = '/Musicade-Logo.png';

interface UnauthorizedPageProps {
  resourceType?: 'service' | 'gig' | 'media' | 'profile' | 'event';
  resourceId?: string;
}

export const UnauthorizedPage: React.FC<UnauthorizedPageProps> = ({
  resourceType = 'content',
  resourceId,
}) => {
  const getResourceLabel = () => {
    switch (resourceType) {
      case 'service':
        return 'Service';
      case 'gig':
        return 'Gig';
      case 'media':
        return 'Media';
      case 'profile':
        return 'Profile';
      case 'event':
        return 'Event';
      default:
        return 'Content';
    }
  };

  const viewLink = (() => {
    if (!resourceId) return '/';
    switch (resourceType) {
      case 'service':
        return `/services/${resourceId}`;
      case 'gig':
        return `/gigs/${resourceId}`;
      case 'media':
        return `/media/${resourceId}`;
      case 'profile':
        return `/profile`;
      case 'event':
        return `/events/${resourceId}`;
      default:
        return '/';
    }
  })();

  return (
    <div className="relative min-h-screen w-full flex bg-[#030303] flex-col overflow-hidden font-['Inter'] selection:bg-purple-500/30">
      {/* Background Glow */}
      <div
        className="absolute pointer-events-none rounded-full"
        style={{
          width: '1308px',
          height: '449px',
          bottom: '-150px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(to right, #DC2626, #F97316)',
          opacity: 0.3,
          filter: 'blur(260px)',
          zIndex: 0,
        }}
      />

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center w-full max-w-[600px] mx-auto px-6 text-center">
        <div className="relative mb-12">
          <span
            className="text-[120px] md:text-[180px] font-black text-white/5 leading-none select-none tracking-tighter"
            style={{ fontFamily: "'Funnel Display', sans-serif" }}
          >
            403
          </span>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-24 h-24 rounded-full bg-red-500/10 flex items-center justify-center border border-red-500/50 shadow-2xl shadow-red-500/20 backdrop-blur-sm">
              <FiLock className="text-red-500" size={48} strokeWidth={1.5} />
            </div>
          </div>
        </div>

        <h1
          className="text-white text-[40px] md:text-[56px] font-bold leading-tight mb-4 tracking-tight"
          style={{ fontFamily: "'Funnel Display', sans-serif" }}
        >
          Not Your Vibe
        </h1>

        <p className="text-zinc-400 text-[18px] md:text-[20px] max-w-[450px] mx-auto mb-4 font-medium leading-relaxed">
          You can only edit {getResourceLabel().toLowerCase()} you created.
        </p>

        <p className="text-zinc-500 text-[14px] md:text-[16px] max-w-[450px] mx-auto mb-12 font-medium leading-relaxed">
          If this belongs to you, try signing in with the account that created it.
        </p>

        <div className="flex flex-col sm:flex-row items-center gap-4">
          {resourceId && (
            <Link
              to={viewLink}
              className="flex items-center justify-center gap-2 px-10 py-4 bg-white text-black rounded-full font-bold text-[16px] hover:bg-zinc-200 transition-all active:scale-[0.98] shadow-xl shadow-white/10 w-full sm:w-auto"
            >
              <FiArrowLeft size={18} />
              View {getResourceLabel()}
            </Link>
          )}

          <Link
            to="/"
            className="px-10 py-4 bg-zinc-900 text-white rounded-full font-bold text-[16px] border border-white/5 hover:bg-zinc-800 transition-all active:scale-[0.98] w-full sm:w-auto"
          >
            Back to Home
          </Link>
        </div>
      </div>

      {/* Footer Branding */}
      <div className="absolute bottom-10 left-0 right-0 flex justify-center opacity-20 select-none">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Musicade Logo" className="w-6 h-6 grayscale" />
          <div className="flex items-center gap-2">
            <span className="text-white font-['Bebas_Neue'] text-[18px] tracking-widest uppercase">
              Musicade
            </span>
            <span className="rounded-full bg-[#2B1A14] px-2.5 py-1 text-[10px] font-semibold leading-none text-[#F7963B]">
              Beta
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
